import React, { useEffect, useState } from "react";
import { apiFetch, getApiBase, parseJsonOrExplain } from "./apiBase";

function formatDate(value) {
  if (!value) return "–";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString("sv-SE");
}

function getErrorCount(row) {
  return Number(row?.error_count || 0);
}

export default function PipelineHistoryPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(null);

  const load = () => {
    setLoading(true);
    setError(null);
    apiFetch(`${getApiBase()}/admin/pipeline/history`)
      .then((r) => parseJsonOrExplain(r))
      .then((data) => setRows(Array.isArray(data?.reports) ? data.reports : []))
      .catch((e) => setError(e.message || "Kunde inte läsa pipelinehistorik"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <section
      style={{
        background: "#fbfaf7",
        border: "1px solid rgba(17,17,17,0.10)",
        borderRadius: "22px",
        padding: "24px",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: "10px", flexWrap: "wrap", alignItems: "center" }}>
        <h2 style={{ margin: 0, fontSize: "30px", fontWeight: 500 }}>Pipelinehistorik</h2>
        <button
          type="button"
          onClick={load}
          style={{
            padding: "8px 12px",
            borderRadius: "999px",
            border: "1px solid rgba(17,17,17,0.2)",
            background: "#fff",
            cursor: "pointer",
          }}
        >
          Ladda om
        </button>
      </div>
      <p style={{ color: "#5f5a52", fontFamily: "Arial, Helvetica, sans-serif" }}>
        Visar senaste körningar (de senaste 7 dagarna) från rapportfilerna, även när inga nya triggers skapats.
      </p>
      {error && <p style={{ color: "#b42318", fontFamily: "Arial, Helvetica, sans-serif" }}>{error}</p>}

      <div style={{ overflowX: "auto", border: "1px solid rgba(17,17,17,0.10)", borderRadius: "14px", background: "#fff" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: "Arial, Helvetica, sans-serif", fontSize: "14px" }}>
          <thead>
            <tr style={{ borderBottom: "1px solid rgba(17,17,17,0.12)" }}>
              <th style={{ textAlign: "left", padding: "12px 14px" }}>Tid</th>
              <th style={{ textAlign: "left", padding: "12px 14px" }}>Run ID</th>
              <th style={{ textAlign: "left", padding: "12px 14px" }}>Status</th>
              <th style={{ textAlign: "left", padding: "12px 14px" }}>Nya triggers</th>
              <th style={{ textAlign: "left", padding: "12px 14px" }}>Fel</th>
              <th style={{ textAlign: "left", padding: "12px 14px" }}>Shortlist/Ranked/Verified</th>
              <th style={{ textAlign: "left", padding: "12px 14px" }}>Rapport</th>
              <th style={{ textAlign: "left", padding: "12px 14px" }}>Detalj</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={8} style={{ padding: "18px 14px", color: "#5f5a52" }}>Laddar historik...</td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={8} style={{ padding: "18px 14px", color: "#5f5a52" }}>Inga rapporter hittades.</td>
              </tr>
            ) : (
              rows.map((r) => {
                const errorCount = getErrorCount(r);
                return (
                <tr key={r.filename} style={{ borderBottom: "1px solid rgba(17,17,17,0.06)" }}>
                  <td style={{ padding: "10px 14px", whiteSpace: "nowrap" }}>{formatDate(r.run_started_at || r.report_created_at)}</td>
                  <td style={{ padding: "10px 14px", fontFamily: "monospace" }}>{r.run_id || "–"}</td>
                  <td style={{ padding: "10px 14px" }}>{r.run_status || "okänd"}</td>
                  <td style={{ padding: "10px 14px", fontWeight: 600, color: Number(r.new_triggers || 0) > 0 ? "#0a7f5a" : "#5f5a52" }}>
                    {r.new_triggers ?? "–"}
                  </td>
                  <td style={{ padding: "10px 14px", fontWeight: 600, color: errorCount > 0 ? "#b42318" : "#5f5a52" }}>
                    {errorCount > 0 ? errorCount : "0"}
                  </td>
                  <td style={{ padding: "10px 14px" }}>
                    {`${r.shortlist_rows ?? "–"} / ${r.ranked_rows ?? "–"} / ${r.verified_rows ?? "–"}`}
                  </td>
                  <td style={{ padding: "10px 14px" }}>{r.filename}</td>
                  <td style={{ padding: "10px 14px" }}>
                    <button
                      type="button"
                      onClick={() => setSelected(r)}
                      style={{
                        padding: "6px 10px",
                        borderRadius: "999px",
                        border: errorCount > 0 ? "1px solid #b42318" : "1px solid rgba(17,17,17,0.2)",
                        background: errorCount > 0 ? "#fff5f5" : "#fff",
                        color: errorCount > 0 ? "#b42318" : "#1f1b16",
                        cursor: "pointer",
                        fontSize: "12px",
                      }}
                    >
                      {errorCount > 0 ? "Visa fel" : "Visa"}
                    </button>
                  </td>
                </tr>
              );
              })
            )}
          </tbody>
        </table>
      </div>

      {selected && (
        <div
          onClick={() => setSelected(null)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(17,17,17,0.45)",
            zIndex: 1200,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "24px",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "min(980px, 96vw)",
              maxHeight: "88vh",
              overflow: "auto",
              background: "#fff",
              borderRadius: "18px",
              border: "1px solid rgba(17,17,17,0.14)",
              boxShadow: "0 24px 80px rgba(17,17,17,0.26)",
              padding: "16px",
              fontFamily: "Arial, Helvetica, sans-serif",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
              <h3 style={{ margin: 0, fontSize: "20px", fontWeight: 600 }}>Rapportdetalj</h3>
              <button
                type="button"
                onClick={() => setSelected(null)}
                style={{ border: "1px solid rgba(17,17,17,0.2)", background: "#fff", borderRadius: "999px", padding: "6px 10px", cursor: "pointer" }}
              >
                Stäng
              </button>
            </div>
            <div style={{ fontSize: "13px", color: "#5f5a52", marginBottom: "8px" }}>
              {selected.filename} · Run ID: {selected.run_id || "–"} · Status: {selected.run_status || "okänd"}
            </div>
            {getErrorCount(selected) > 0 && (
              <div
                style={{
                  marginBottom: "12px",
                  padding: "12px",
                  borderRadius: "10px",
                  border: "1px solid rgba(180,35,24,0.25)",
                  background: "#fff5f5",
                  color: "#7a1b12",
                  fontSize: "13px",
                }}
              >
                <div style={{ fontWeight: 700, marginBottom: "8px" }}>
                  {getErrorCount(selected)} fel i denna körning
                </div>
                {(selected.error_groups || []).length > 0 ? (
                  <div style={{ marginBottom: "10px" }}>
                    {(selected.error_groups || []).map((g) => (
                      <div key={g.message} style={{ marginBottom: "6px" }}>
                        <strong>{g.message}</strong> ({g.count} st)
                        {Array.isArray(g.companies) && g.companies.length > 0 && (
                          <div style={{ marginTop: "2px", color: "#5f5a52" }}>
                            {g.companies.join(", ")}
                            {g.count > g.companies.length ? ` (+${g.count - g.companies.length} till)` : ""}
                          </div>
                        )}
                        {g.example ? (
                          <div style={{ marginTop: "4px", fontFamily: "ui-monospace, monospace", fontSize: "12px", whiteSpace: "pre-wrap" }}>
                            {g.example}
                          </div>
                        ) : null}
                      </div>
                    ))}
                  </div>
                ) : null}
                {(selected.errors || []).length > 0 && (
                  <div>
                    <div style={{ fontWeight: 600, marginBottom: "6px" }}>Detaljer per bolag</div>
                    {(selected.errors || []).slice(0, 20).map((err) => (
                      <div key={`${err.company_name}-${err.updated_at}`} style={{ marginBottom: "8px" }}>
                        <div style={{ fontWeight: 600 }}>{err.company_name}</div>
                        <div style={{ fontFamily: "ui-monospace, monospace", fontSize: "12px", whiteSpace: "pre-wrap" }}>
                          {String(err.last_error_text || "").trim() || "Inget felmeddelande sparat."}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {selected.error_section ? (
                  <pre
                    style={{
                      margin: "10px 0 0",
                      whiteSpace: "pre-wrap",
                      fontSize: "12px",
                      lineHeight: 1.35,
                      fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
                    }}
                  >
                    {selected.error_section}
                  </pre>
                ) : null}
              </div>
            )}
            <pre
              style={{
                margin: 0,
                whiteSpace: "pre-wrap",
                fontSize: "12px",
                lineHeight: 1.35,
                color: "#1f1b16",
                background: "#fcfbf8",
                border: "1px solid rgba(17,17,17,0.10)",
                borderRadius: "10px",
                padding: "10px",
                maxHeight: "65vh",
                overflow: "auto",
                fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
              }}
            >
              {selected.report_text || selected.report_preview || "Ingen preview tillgänglig."}
            </pre>
          </div>
        </div>
      )}
    </section>
  );
}

